import { Component, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { StripeCardComponent, StripeService } from 'ngx-stripe';
import { updateCardMessages } from '@app/configs/app-message.config';
import { CommonService } from '@app/core/services/common.service';
import { StripePaymentService } from '@app/modules/account/service/stripe.service';
import { getFriendlyStripeErrorMessage } from '@app/modules/account/utils/stripe-error-message.util';
import { SavedCard } from '@app/shared/models/subscription-renewal.model';

/**
 * Lets the owner replace the saved card. The card number and CVC go straight from Stripe's card field to Stripe;
 * only the token reaches the backend. The dialog closes with the new masked card, or without a value when cancelled.
 */
@Component({
  selector: 'app-update-card-dialog',
  templateUrl: './update-card-dialog.component.html',
  styleUrls: ['./update-card-dialog.component.scss']
})
export class UpdateCardDialogComponent {
  @ViewChild(StripeCardComponent) card: StripeCardComponent;

  readonly messages = updateCardMessages;
  readonly cardOptions: StripeCardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        fontWeight: '300',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '18px',
        '::placeholder': { color: '#CFD7E0' }
      }
    }
  };
  readonly elementsOptions: StripeElementsOptions = {};

  saving = false;
  errorMessage = '';
  /** True once Stripe's card field reports a complete, valid card. */
  cardComplete = false;

  constructor(
    private dialogRef: MatDialogRef<UpdateCardDialogComponent, SavedCard>,
    private stripeService: StripeService,
    private stripePaymentService: StripePaymentService,
    private commonService: CommonService
  ) { }

  get canSave(): boolean {
    return this.cardComplete && !this.saving;
  }

  onCardChange(event: { complete?: boolean; error?: { message?: string } }): void {
    this.cardComplete = !!event && !!event.complete;
    this.errorMessage = event && event.error && event.error.message ? event.error.message : '';
  }

  onSave(): void {
    if (!this.canSave) {
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    this.stripeService.createToken(this.card.element).subscribe(
      (result) => {
        if (result && result.token) {
          this.saveToken(result.token.id);
        } else {
          this.fail((result && result.error && result.error.message) || updateCardMessages.failure);
        }
      },
      (error) => this.fail(getFriendlyStripeErrorMessage(error))
    );
  }

  onCancel(): void {
    if (!this.saving) {
      this.dialogRef.close();
    }
  }

  private saveToken(tokenId: string): void {
    this.stripePaymentService.updateCard(tokenId, localStorage.getItem('userToken')).subscribe(
      (res) => {
        if (res && (Number(res.statusCode) === 200 || Number(res.statusCode) === 201) && res.data) {
          this.saving = false;
          this.commonService.showSuccessSnakeBar(updateCardMessages.success);
          this.dialogRef.close(res.data);
        } else {
          this.fail((res && res.message) || updateCardMessages.failure);
        }
      },
      (error) => this.fail((error && error.error && error.error.message) || updateCardMessages.failure)
    );
  }

  /** The saved card is unchanged on any failure, so the dialog stays open for another try. */
  private fail(message: string): void {
    this.saving = false;
    this.errorMessage = message;
  }
}
